import { clsx } from 'clsx';
import type { MouseEvent, SyntheticEvent } from 'react';
import { memo, useCallback, useRef } from 'react';
import { slowCn } from '../../lib/utils';
import { useSlot } from '../../slots';
import { Box } from '../Box';
import { Popover } from '../Popover';
import MenuContent from './MenuContent';
import type { MenuItemProps } from './MenuItem';
import { menuItemIconVariants, menuItemTitleVariants } from './styles';
import type { MenuListComponentProps, MenuListItem } from './types';

// forward declaration to avoid circular dependency
export type GroupMenuItemProps = Omit<MenuItemProps, 'onClick'> & {
  items: MenuListItem[];
  onAction: (id: string, event: MouseEvent | SyntheticEvent) => void;
  MenuListComponent: React.ComponentType<MenuListComponentProps>;
  isOpen: boolean;
  onToggle: (id: string, shouldOpen: boolean) => void;
};

const BOX_STYLES = { display: 'contents' };

const GroupMenuItem = memo(function GroupMenuItem(props: GroupMenuItemProps) {
  const {
    title,
    className,
    menuContentContainerClassName,
    helpTextDetails,
    onMouseEnter,
    variant = 'default',
    startDecoratorComponent: StartDecoratorComponent,
    startDecoratorNode,
    endDecoratorComponent: EndDecoratorComponent,
    endDecoratorNode,
    disabled = false,
    selected,
    size = 'md',
    slots,
    caption,
    titleClassName: _titleClassName,
    index,
    enableHoverEffect = true,
    id,
    items,
    onAction,
    MenuListComponent,
    isOpen,
    onToggle,
  } = props;

  const anchorRef = useRef<HTMLElement | null>(null);
  const setAnchorEl = useCallback((node: HTMLElement | null) => {
    anchorRef.current = node;
  }, []);

  const iconClassName = menuItemIconVariants({ variant, disabled });
  const titleClassName = menuItemTitleVariants({
    variant,
    disabled,
    className: _titleClassName,
  });

  const startDecorator = StartDecoratorComponent ? (
    <StartDecoratorComponent className={iconClassName} height={16} width={16} />
  ) : (
    startDecoratorNode
  );

  const endDecorator = EndDecoratorComponent ? (
    <EndDecoratorComponent
      className={clsx('justify-self-end self-center', iconClassName)}
      height={16}
      width={16}
    />
  ) : (
    endDecoratorNode
  );

  const handleNestedAction = useCallback(
    (actionId: string, event: MouseEvent) => {
      onAction(actionId, event);
      onToggle(id || '', false);
    },
    [onAction, onToggle, id],
  );

  const [MenuContentItem, menuContentItemProps] = useSlot('menuContentItem', {
    elementType: MenuContent,
    className: slowCn(
      'row bg-primary my-xxs mx-sm ps-md pe-md flex flex-row items-center rounded-sm',
      { 'cursor-pointer': true },
      { 'hover:bg-secondary_hover': enableHoverEffect },
      { 'pointer-events-none': disabled },
      { 'bg-quaternary': selected || isOpen },
      { 'py-md !h-9': size === 'md' },
      { 'py-sm !h-7 !min-h-7': size === 'sm' },
      className,
    ),
    additionalProps: {
      startDecorator,
      endDecorator,
      title,
      titleClassName,
      menuContentContainerClassName,
      helpTextDetails,
      caption,
      size,
      onClick: () => onToggle(id || '', !isOpen),
      onMouseEnter,
      disabled,
      selected,
      index,
      id,
      ref: setAnchorEl,
    },
    externalForwardedProps: { slots },
    ownerState: {},
  });

  const handleOpenChange = useCallback(
    (open: boolean) => {
      onToggle(id || '', open);
    },
    [onToggle, id],
  );

  if (items.length === 0) {
    return null;
  }

  return (
    <Popover onOpenChange={handleOpenChange} open={isOpen}>
      {/* wrapper captures hover and forwards to your parent onMouseEnter handler */}
      <Box onMouseEnter={(e) => onMouseEnter?.(e as MouseEvent)} style={BOX_STYLES}>
        <Popover.Button>
          <MenuContentItem {...menuContentItemProps} ref={setAnchorEl} />
        </Popover.Button>
      </Box>
      <Popover.Content placement="right-start">
        <MenuListComponent items={items} onAction={handleNestedAction} size={size} />
      </Popover.Content>
    </Popover>
  );
});

export default GroupMenuItem;
